import axios from 'axios';
import apolloClient from '../apolloClient';
import { GET_BANK_TRANSACTIONS, ALLOCATE_RULE_MATCHED_TRANSACTION } from '../graphql/queries';
import * as constants from './types';
import moment from 'moment';

/**
 * Helper function to fetch transactions from REST API and enrich with matched rules from GraphQL
 * @param {string} bookId - The book ID
 * @param {string} dateRange - The date range for transactions (format: "?dateFrom=TIMESTAMP&dateTo=TIMESTAMP")
 * @param {object} account - The account object with value property
 * @param {object} headers - Request headers
 * @returns {Promise<object>} - Response data with enriched transactions
 */
export async function fetchTransactionsWithRules(bookId, dateRange, account, headers) {
  try {
    // Step 1: Fetch transactions from REST API
    const restResponse = await axios.get(
      `${constants.ROOT_URL}/books/${bookId}/transactions/${dateRange}&account=${account.value}`,
      { headers }
    );

    const transactions = (restResponse.data && restResponse.data.transactions) || [];
    
    // Step 2: Parse date range to extract dateFrom and dateTo
    // Format expected: "?dateFrom=TIMESTAMP&dateTo=TIMESTAMP"
    const dateParams = dateRange.match(/dateFrom=(\d+)&dateTo=(\d+)/);

    if (!dateParams || transactions.length === 0) {
      // If we can't parse dates or no transactions, return without matched rules
      return restResponse.data;
    }

    const dateFromISO = moment.unix(dateParams[1]).toISOString();
    const dateToISO = moment.unix(dateParams[2]).toISOString();

    // Step 3: Fetch transactions with matched rules from GraphQL
    const variables = {
      bookId,
      input: {
        bookId,
        bankAccountId: account.bankId,
        dateFrom: dateFromISO,
        dateTo: dateToISO,
      },
    };
    
    const gqlResponse = await apolloClient.query({
      query: GET_BANK_TRANSACTIONS,
      variables,
      fetchPolicy: 'network-only',
    });

    const gqlTransactions = (gqlResponse.data && gqlResponse.data.book && gqlResponse.data.book.bankTransactions) || [];
    
    // Step 4: Filter to only transactions with matchedRule, excluding bank transfer rules
    const transactionsWithRules = gqlTransactions.filter(t => t.matchedRule && !t.matchedRule.bankTransferBankAccountId);

    if (transactionsWithRules.length === 0) {
      // No matched rules found, return original data
      return restResponse.data;
    }

    // Step 5: Create a map for quick lookup by transactionId
    const ruleMap = new Map();
    transactionsWithRules.forEach(t => {
      ruleMap.set(t.transactionId.toUpperCase(), t.matchedRule);
    });

    // Step 6: Merge matched rules into REST API transactions
    const enrichedTransactions = transactions.map(transaction => {
      const matchedRule = ruleMap.get(transaction.BankDataGUID.toUpperCase());
      if (matchedRule) {
        return {
          ...transaction,
          matchedRule,
        };
      }
      return transaction;
    });

    // Return the response with enriched transactions
    return {
      ...restResponse.data,
      transactions: enrichedTransactions,
    };
  } catch (error) {
    console.error('Error fetching transactions with rules:', error);
    // If GraphQL fails, still return REST data
    try {
      const restResponse = await axios.get(
        `${constants.ROOT_URL}/books/${bookId}/transactions/${dateRange}&account=${account.value}`,
        { headers }
      );
      return restResponse.data;
    } catch (restError) {
      throw restError; // Re-throw REST error if it also fails
    }
  }
}

/**
 * Helper function to accept a rule matched transaction
 * @param {string} bookId - The book ID
 * @param {string} bankAccountId - The bank account ID
 * @param {object} transaction - The transaction object with matchedRule
 * @returns {Promise<object>} - Mutation result
 */
export async function acceptRuleMatchedTransaction(bookId, bankAccountId, transaction) {
  try {
    // Parse transaction date to ISO format if needed
    const transactionDate = transaction.TransactionDate 
      ? moment.utc(transaction.TransactionDate, 'D MMM YY').toISOString()
      : null;

    const variables = {
      input: {
        bankAccountId,
        bookId,
        ruleMatchedTransactions: [
          {
            ruleId: transaction.matchedRule.id,
            transactionAmount: transaction.Amount,
            transactionDate,
            transactionId: transaction.BankDataGUID,
          }
        ],
      },
    };

    const response = await apolloClient.mutate({
      mutation: ALLOCATE_RULE_MATCHED_TRANSACTION,
      variables,
    });

    return response.data.allocateRuleMatchedTransaction;
  } catch (error) {
    console.error('Error allocating rule matched transaction:', error);
    throw error;
  }
}



// WEBPACK FOOTER //
// ./src/Actions/transactionHelpers.js